import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Menu, X, Globe, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { companyPages } from "@/i18n/companyPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { certificates, type Category } from "@/data/certificates";
import { certificatePathFor } from "@/data/certificateSlugs";
import {
  companyCatalogGroups,
  companyInquiryHref,
  companyPaths,
  type CompanyLang,
} from "@/data/company";
import { drugTestingHref } from "@/data/drugTesting";
import logo from "@/assets/logo.svg";

const categoryOrder: Category[] = [
  "korkort", "alkohol", "sjofolk", "arbete", "resor", "forsakring", "sport", "adoption", "afs", "ovriga",
];

const categoryPageHref = (category: Category, lang: "sv" | "en") =>
  `${lang === "sv" ? "/starta-arende" : "/en/start-case"}?omrade=${category}`;

export const Header = () => {
  const { t, prefix, lang, switchHref } = useLang();
  const location = useLocation();
  const companyLang = lang as CompanyLang;
  const companyCopy = companyPages[lang];
  const bookingHref = lang === "sv" ? "/boka-tid" : "/en/book-appointment";
  const [open, setOpen] = useState(false);
  const [mobileCertOpen, setMobileCertOpen] = useState(false);
  const [desktopCertOpen, setDesktopCertOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const closeMobileMenu = () => {
    setOpen(false);
    setMobileCertOpen(false);
  };

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.body.setAttribute("data-mobile-nav-open", "");
      return () => {
        document.body.style.overflow = prev;
        document.body.removeAttribute("data-mobile-nav-open");
      };
    }
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setMobileCertOpen(false);
    setDesktopCertOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return certificates
      .filter((cert) => {
        const haystack = [cert.title[lang], cert.description[lang], t.categories[cert.category]]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8);
  }, [lang, searchQuery, t.categories]);

  const privateLinks = [
    { to: lang === "sv" ? "/halsokontroller" : "/en/health-checks", label: t.nav.checkups },
    { to: lang === "sv" ? "/drogtestning" : "/en/drug-testing", label: t.nav.drugTesting },
    { to: lang === "sv" ? "/infor-besoket" : "/en/before-visit", label: t.nav.visit },
    { to: lang === "sv" ? "/om-oss" : "/en/about-us", label: t.about.eyebrow },
    { to: lang === "sv" ? "/fragor-och-svar" : "/en/faq", label: t.nav.faq },
    { to: lang === "sv" ? "/kontakt" : "/en/contact", label: t.nav.contact },
  ];

  const privateHref = lang === "sv" ? "/" : "/en";
  const companyHref = companyPaths.hub[companyLang];
  const isCompanyArea =
    location.pathname.startsWith("/foretag") || location.pathname.startsWith("/en/companies");
  const hideCompanyCta = false;

  const companyDesktopLinks = [
    { to: companyPaths.tjanster[companyLang], label: companyCopy.nav.allServices },
    { to: companyPaths.arbetsmiljo[companyLang], label: companyCopy.nav.arbetsmiljo },
    { to: companyPaths.drogtestning[companyLang], label: companyCopy.nav.drogtestning },
    { to: companyPaths.bokaTid[companyLang], label: companyCopy.nav.bokaTid },
  ];

  const companyMobileTiles = [
    { to: companyPaths.arbetsmiljo[companyLang], label: companyCopy.nav.arbetsmiljo },
    { to: companyPaths.drogtestning[companyLang], label: companyCopy.nav.drogtestning },
    ...companyCatalogGroups.map((group) => ({
      to: `${companyPaths.tjanster[companyLang]}#kategori-${group.id}`,
      label: group.title[companyLang],
    })),
  ];

  const audienceLinkClass = (active: boolean) =>
    cn(
      "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-base",
      active
        ? "border-white bg-white text-primary"
        : "border-white/25 text-white/90 hover:border-white/60 hover:text-white",
    );

  const logoHref = isCompanyArea ? companyHref : `${prefix}/`;
  const primaryCtaHref = isCompanyArea ? companyInquiryHref(companyLang) : bookingHref;
  const primaryCtaLabel = isCompanyArea ? companyCopy.shared.inquiryCta : t.nav.book;

  return (
    <div className="sticky top-0 z-50">
      <div className="w-full bg-primary px-3 py-2 sm:px-5 lg:px-8">
        <div className="relative mx-auto flex max-w-7xl items-center justify-center">
          <p className="text-center text-xs font-medium tracking-wide text-primary-foreground/90 lg:px-48 sm:text-sm">
            {t.nav.topBanner}
          </p>
          <div className="absolute right-0 hidden items-center gap-2 lg:flex">
            <a href={privateHref} className={audienceLinkClass(!isCompanyArea)}>
              {t.nav.private}
            </a>
            <a href={companyHref} className={audienceLinkClass(isCompanyArea)}>
              {t.nav.company}
            </a>
            <Link
              to={switchHref}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-2.5 py-1 text-xs font-medium text-white/90 transition-base hover:border-white/60 hover:text-white"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "sv" ? "EN" : "SV"}
            </Link>
          </div>
        </div>
      </div>
      <header className="w-full px-3 pt-3 sm:px-5 sm:pt-4 lg:px-6">
        <div className="relative mx-auto max-w-[88rem]">
          <div
            aria-hidden
            className={cn("pointer-events-none absolute inset-0 border border-primary/40 bg-primary shadow-card", searchOpen ? "rounded-t-2xl sm:rounded-t-3xl" : "rounded-2xl sm:rounded-3xl")}
          />
          <div className={cn("relative z-10 overflow-hidden text-primary-foreground transition-all duration-300", searchOpen ? "rounded-t-2xl sm:rounded-t-3xl" : "rounded-2xl sm:rounded-3xl")}>
            <div className="flex h-14 items-center justify-between gap-4 px-4 sm:h-16 sm:px-6">              <Link to={logoHref} aria-label="Intygsläkaren" className="flex items-center">
                <img
                  src={logo}
                  alt="Intygsläkaren"
                  width={151}
                  height={36}
                  className="logo-white h-9 w-auto"
                />
              </Link>

              <nav className="hidden items-center gap-1 lg:flex">
                {isCompanyArea ? (
                  companyDesktopLinks.map((l) => (
                    <a
                      key={l.to}
                      href={l.to}
                      className="rounded-md px-3 py-2 text-sm font-medium text-primary-foreground/85 transition-base hover:text-primary-foreground"
                    >
                      {l.label}
                    </a>
                  ))
                ) : (
                  <>
                    <div
                      className="relative"
                      onMouseEnter={() => setDesktopCertOpen(true)}
                      onMouseLeave={() => setDesktopCertOpen(false)}
                    >
                      <Link
                        to={`${prefix}/lakarintyg`}
                        onClick={() => setDesktopCertOpen(false)}
                        className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-primary-foreground/85 transition-base hover:text-primary-foreground"
                      >
                        {t.nav.certificates}
                        <ChevronDown
                          className={cn("h-3.5 w-3.5 transition-transform", desktopCertOpen && "rotate-180")}
                        />
                      </Link>
                      <div
                        className={cn(
                          "fixed inset-x-3 top-[104px] z-50 pt-3 transition-all duration-200 sm:inset-x-5 sm:top-[116px] lg:inset-x-8",
                          desktopCertOpen ? "visible opacity-100" : "invisible opacity-0",
                        )}
                      >
                        <div className="mx-auto w-full max-w-[88rem] overflow-hidden rounded-2xl border border-primary/40 bg-primary text-primary-foreground shadow-elevated sm:rounded-3xl">
                          <div className="grid grid-cols-5 gap-px p-3">
                            {categoryOrder.map((cat) => {
                              const items = certificates.filter((c) => c.category === cat).slice(0, 3);
                              const extraLinks =
                                cat === "alkohol"
                                  ? [
                                      {
                                        to: companyPaths.drogtestning[companyLang],
                                        label: lang === "sv" ? "Drogtestning för företag" : "Drug testing for companies",
                                      },
                                    ]
                                  : [];
                              return (
                                <div
                                  key={cat}
                                  className="group/cat flex flex-col gap-1.5 rounded-xl p-2.5 transition-base hover:bg-primary-foreground/10"
                                >
                                  <Link
                                    to={categoryPageHref(cat, lang)}
                                    onClick={() => setDesktopCertOpen(false)}
                                    className="font-display text-sm font-semibold text-primary-foreground"
                                  >
                                    {t.categories[cat]}
                                  </Link>
                                  <ul className="space-y-0.5">
                                    {items.map((it) => (
                                      <li key={it.slug} className="truncate">
                                        <Link
                                          to={certificatePathFor(it.slug, lang)}
                                          onClick={() => setDesktopCertOpen(false)}
                                          className="text-xs text-primary-foreground/65 transition-base hover:text-primary-foreground"
                                        >
                                          {it.title[lang]}
                                        </Link>
                                      </li>
                                    ))}
                                    {extraLinks.map((item) => (
                                      <li key={item.to} className="truncate">
                                        <Link
                                          to={item.to}
                                          onClick={() => setDesktopCertOpen(false)}
                                          className="text-xs text-primary-foreground/65 transition-base hover:text-primary-foreground"
                                        >
                                          {item.label}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-between border-t border-primary-foreground/15 bg-primary-foreground/5 px-5 py-3">
                            <span className="text-xs text-primary-foreground/70">
                              {lang === "sv"
                                ? "30+ olika läkarintyg — fasta priser"
                                : "30+ certificate types — fixed pricing"}
                            </span>
                            <Link
                              to={`${prefix}/lakarintyg`}
                              onClick={() => setDesktopCertOpen(false)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-foreground hover:underline"
                            >
                              {lang === "sv" ? "Se alla intyg" : "See all certificates"}
                              <ArrowUpRight className="h-3 w-3" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>

                    {privateLinks.map((l) => (
                      <a
                        key={l.label}
                        href={l.to}
                        className="rounded-md px-3 py-2 text-sm font-medium text-primary-foreground/85 transition-base hover:text-primary-foreground"
                      >
                        {l.label}
                      </a>
                    ))}
                  </>
                )}
              </nav>

              <div className="flex items-center gap-2">
                <div className="hidden items-center gap-2 lg:flex">
                  {!(isCompanyArea && hideCompanyCta) ? (
                    <Button asChild variant="secondary" size="lg">
                      <a href={primaryCtaHref}>{primaryCtaLabel}</a>
                    </Button>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSearchOpen((value) => !value);
                    setDesktopCertOpen(false);
                    if (searchOpen) setSearchQuery("");
                  }}
                  aria-label={searchOpen ? t.nav.searchClose : t.nav.searchOpen}
                  aria-expanded={searchOpen}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-foreground transition-base hover:bg-primary-foreground/10"
                >
                  {searchOpen ? <XMarkIcon className="h-5 w-5" /> : <MagnifyingGlassIcon className="h-5 w-5" />}
                </button>
                <button
                  onClick={() => setOpen((o) => !o)}
                  aria-label="Menu"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-foreground lg:hidden"
                >
                  {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Search panel — absolutely positioned so it doesn't affect sticky header height */}
          {searchOpen ? (
            <div className="absolute inset-x-0 top-full z-20 rounded-b-2xl bg-primary px-4 pb-4 pt-2 text-primary-foreground shadow-elevated sm:rounded-b-3xl sm:px-6">
              <div className="group flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 p-1.5 transition-base focus-within:border-primary-foreground/40 focus-within:bg-primary-foreground/15">
                <div className="relative min-w-0 flex-1">
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-primary-foreground/60 transition-base group-focus-within:text-primary-foreground/90"
                    aria-hidden
                  />
                  <Input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.nav.searchPlaceholder}
                    aria-label={t.nav.searchOpen}
                    className="h-12 w-full border-0 bg-transparent pl-12 pr-10 text-base text-primary-foreground shadow-none placeholder:text-primary-foreground/55 focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-primary-foreground/60 transition-base hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      aria-label={t.nav.searchClose}
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden />
                    </button>
                  ) : null}
                </div>
              </div>
              {searchQuery.trim() ? (
                <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-primary-foreground/15 bg-primary-foreground/5">
                  {searchResults.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-primary-foreground/70">{t.nav.searchNoResults}</p>
                  ) : (
                    <ul>
                      {searchResults.map((cert) => (
                        <li key={cert.slug}>
                          <Link
                            to={certificatePathFor(cert.slug, lang)}
                            onClick={() => {
                              setSearchOpen(false);
                              setSearchQuery("");
                            }}
                            className="flex items-start justify-between gap-3 border-b border-primary-foreground/10 px-4 py-3 transition-base last:border-b-0 hover:bg-primary-foreground/10"
                          >
                            <span>
                              <span className="block text-sm font-medium text-primary-foreground">
                                {cert.title[lang]}
                              </span>
                              <span className="mt-0.5 block text-xs text-primary-foreground/65">
                                {t.categories[cert.category]} · {cert.price[lang]}
                              </span>
                            </span>
                            <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-primary-foreground/50" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            "fixed inset-0 z-40 bg-primary text-primary-foreground transition-all duration-300 lg:hidden",
            open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-foreground"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex h-full flex-col overflow-y-auto px-6 pb-8 pt-24">
            {isCompanyArea ? (
              <>
                <a
                  href={companyPaths.hub[companyLang]}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-2xl font-display font-semibold text-primary-foreground"
                >
                  {companyCopy.nav.overview}
                </a>
                <a
                  href={companyPaths.tjanster[companyLang]}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-2xl font-display font-semibold text-primary-foreground"
                >
                  {companyCopy.nav.allServices}
                </a>
                <div className="grid grid-cols-2 gap-2 py-4">
                  {companyMobileTiles.map((item) => (
                    <a
                      key={item.to}
                      href={item.to}
                      onClick={closeMobileMenu}
                      className="rounded-xl border border-primary-foreground/20 px-3 py-3 text-sm text-primary-foreground/90"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
                <a
                  href={companyInquiryHref(companyLang, "flera-tjanster")}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-lg font-medium text-primary-foreground/90"
                >
                  {companyCopy.nav.otherCertificates}
                </a>
                <a
                  href={lang === "sv" ? "/halsokontroller" : "/en/health-checks"}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-lg font-medium text-primary-foreground/90"
                >
                  {companyCopy.nav.healthChecks}
                </a>
                <a
                  href={lang === "sv" ? "/om-oss" : "/en/about-us"}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-lg font-medium text-primary-foreground/90"
                >
                  {companyCopy.nav.about}
                </a>
                <a
                  href={companyPaths.kontakt[companyLang]}
                  onClick={closeMobileMenu}
                  className="border-b border-primary-foreground/15 py-4 text-lg font-medium text-primary-foreground/90"
                >
                  {companyCopy.nav.kontakt}
                </a>
              </>
            ) : (
              <>
                <button
                  onClick={() => setMobileCertOpen((v) => !v)}
                  className="flex items-center justify-between border-b border-primary-foreground/15 py-4 text-left text-2xl font-display font-semibold text-primary-foreground"
                >
                  {t.nav.certificates}
                  <ChevronDown className={cn("h-5 w-5 transition-transform", mobileCertOpen && "rotate-180")} />
                </button>
                {mobileCertOpen && (
                  <div className="grid grid-cols-2 gap-2 py-4">
                    {categoryOrder.map((cat) => (
                      <Link
                        key={cat}
                        to={categoryPageHref(cat, lang)}
                        onClick={closeMobileMenu}
                        className="rounded-xl border border-primary-foreground/20 px-3 py-2 text-sm text-primary-foreground/90"
                      >
                        {t.categories[cat]}
                      </Link>
                    ))}
                  </div>
                )}
                {privateLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.to}
                    onClick={closeMobileMenu}
                    className="border-b border-primary-foreground/15 py-4 text-2xl font-display font-semibold text-primary-foreground"
                  >
                    {l.label}
                  </a>
                ))}
              </>
            )}

            <div className="mt-auto flex flex-col gap-3 pt-8">
              <div className="flex gap-2">
                <a
                  href={privateHref}
                  onClick={closeMobileMenu}
                  className={cn(audienceLinkClass(!isCompanyArea), "flex-1 justify-center py-2.5 text-sm")}
                >
                  {t.nav.private}
                </a>
                <a
                  href={companyHref}
                  onClick={closeMobileMenu}
                  className={cn(audienceLinkClass(isCompanyArea), "flex-1 justify-center py-2.5 text-sm")}
                >
                  {t.nav.company}
                </a>
              </div>
              {!(isCompanyArea && hideCompanyCta) ? (
                <Button asChild variant="secondary" size="xl" className="w-full">
                  <a href={primaryCtaHref} onClick={closeMobileMenu}>
                    {primaryCtaLabel}
                  </a>
                </Button>
              ) : null}
              <div className="flex items-center justify-between text-sm text-primary-foreground/80">
                <Link
                  to={switchHref}
                  onClick={closeMobileMenu}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary-foreground/30 px-3 py-1.5"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {lang === "sv" ? "English" : "Svenska"}
                </Link>
                <a href="tel:+46317617700" className="inline-flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  031-761 77 00
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};
